import { ref, computed, watch, inject, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import type { BuyRequest } from '@/js/services/RogueTypes'

export interface UseUnitAccordionsOptions {
  isUnit: (child: BuyRequest) => boolean
  // Runs before unitNumber/unitCollection are computed, so a write like .height is visible in the snapshot.
  decorateUnit?: (child: BuyRequest, openAccordionIndex: Ref<number | null>) => void
  onBeforeAdd?: (newUnit: BuyRequest, currentUnitCount: number, side?: 'left' | 'right') => void
  onAfterRemove?: (updatedBuyRequest: BuyRequest) => void
  // Fires after the unit-id counter has already been re-seeded for the new buyRequest.
  onRidChange?: () => void
}

export function useUnitAccordions(buyRequest: Ref<BuyRequest>, opts: UseUnitAccordionsOptions) {
  const route = useRoute()
  const updateBuyRequest = inject<(val: BuyRequest) => void>('updateBuyRequest')

  const unitCounter = ref(0)
  const openAccordionIndex = ref<number | null>(0)
  const unitPendingDeletionId = ref<number | null>(null)

  // Captured once at mount — re-reading children[1] live would clone a shuffled unit after a delete.
  const pristineUnitTemplate: BuyRequest | undefined = buyRequest.value.children?.[1]
    ? JSON.parse(JSON.stringify(buyRequest.value.children[1]))
    : undefined

  function getFilteredUnits(br: BuyRequest) {
    return (br.children ?? []).filter(opts.isUnit)
  }

  function seedUnitCounter() {
    const existingIds = getFilteredUnits(buyRequest.value)
      .map(child => child.metadata?.id)
      .filter((id): id is number => typeof id === 'number')

    if (existingIds.length > 0) {
      unitCounter.value = Math.max(...existingIds)
    }
  }

  // Runs before units is ever evaluated, so id-assignment never depends on watcher order.
  seedUnitCounter()

  function updateUnitMetadata(units: BuyRequest[]) {
    units.forEach((child, index) => {
      child.metadata ??= {}

      opts.decorateUnit?.(child, openAccordionIndex)

      // Read by m2-checkout (recipe lines sort by it) and the order-confirmation email; a host whose base unit is not one of these children (Hybrid's rack) renumbers in its variant's prepareForCart.
      child.metadata.unitNumber = index + 1

      child.metadata.unitCollection = units.map(item => ({
        id        : item.metadata?.id,
        productId : item.productId,
        qty       : item.qty,
        height    : item.metadata?.height,
      }))
    })
  }

  const units = computed(() => {
    const unitBRs = getFilteredUnits(buyRequest.value)

    unitBRs.forEach((child) => {
      if (!child.metadata) child.metadata = {}
      if (!child.metadata?.id) {
        unitCounter.value += 1
        child.metadata!.id = unitCounter.value
      }
    })

    updateUnitMetadata(unitBRs)

    unitBRs.filter(unit => unit.qty === 0).forEach(unit => unit.qty = 1)

    return unitBRs.map(child => ({
      id: child.metadata!.id,
      child,
    }))
  })

  const validBundleUnitBRs = computed(() => getFilteredUnits(buyRequest.value))

  function addUnit(side?: 'left' | 'right') {
    if (!pristineUnitTemplate) return

    const newBR: BuyRequest = JSON.parse(JSON.stringify(pristineUnitTemplate))
    const updatedBuyRequest: BuyRequest = JSON.parse(JSON.stringify(buyRequest.value))
    updatedBuyRequest.children = updatedBuyRequest.children ?? []

    const currentUnitCount = units.value.length

    if (!newBR.metadata) newBR.metadata = {}
    // Template may have been cloned from a restored ?rid= unit that already carries a real id — strip it so units' self-heal assigns a fresh one.
    delete newBR.metadata.id
    if (side) newBR.metadata.side = side

    opts.onBeforeAdd?.(newBR, currentUnitCount, side)

    updatedBuyRequest.children.push(newBR)
    openAccordionIndex.value = currentUnitCount

    updateBuyRequest?.(updatedBuyRequest)
  }

  function displayModal(id: number) {
    unitPendingDeletionId.value = id
  }

  function removeUnit(id: number) {
    unitPendingDeletionId.value = null

    const updatedBuyRequest: BuyRequest = JSON.parse(JSON.stringify(buyRequest.value))

    if (updatedBuyRequest.children) {
      updatedBuyRequest.children = updatedBuyRequest.children.filter(child => child.metadata?.id !== id)
    }

    opts.onAfterRemove?.(updatedBuyRequest)

    updateBuyRequest?.(updatedBuyRequest)
  }

  watch(() => route.query.rid, (newRid, oldRid) => {
    if (newRid && newRid !== oldRid) {
      seedUnitCounter()
      opts.onRidChange?.()
    }
  }, { immediate: true, deep: true })

  return {
    units,
    validBundleUnitBRs,
    openAccordionIndex,
    unitPendingDeletionId,
    addUnit,
    displayModal,
    removeUnit,
  }
}
